import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import {
  Bell,
  CheckCheck,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  Inbox,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter49 — Full notifications page.
 *
 * Bug from tester ~S: "When we click 'View all notifications' button in the
 * bell popup, the popup closes immediately." Root cause: the button was a
 * no-op stub that only called `setIsOpen(false)` and never navigated anywhere
 * (`NotificationBell.jsx` ~L200 had a TODO comment "Could navigate to a full
 * notifications page if needed"). This page is that destination — paginated,
 * filterable, with mark-as-read and mark-all-read.
 */
export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread | <type>
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Pull a generous slice; the bell only fetches 10, this page wants the
      // full picture. Backend hard-caps at whatever it likes; 200 is plenty
      // for an HR tenant.
      const res = await axios.get(`${API}/notifications?limit=200`);
      setNotifications(res.data?.notifications || []);
      setUnreadCount(res.data?.unread_count || 0);
    } catch (e) {
      toast.error('Could not load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      // optimistic update so the UI doesn't have to wait for a refetch
      setNotifications((prev) =>
        prev.map((n) => (n.notification_id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // silent — the next fetchAll will resync
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Could not mark all as read');
    }
  };

  const handleRowClick = (n) => {
    if (!n.read) markRead(n.notification_id);
    // Iter49 (tester nit) — only follow relative in-app paths. A future bug
    // or malformed seed could stash an absolute URL on `action_url`;
    // navigate() would happily try to load it. Restrict to in-app routes.
    if (n.action_url && typeof n.action_url === 'string' && n.action_url.startsWith('/')) {
      navigate(n.action_url);
    }
  };

  // Distinct type list for the filter chips, in stable order.
  const availableTypes = useMemo(() => {
    const set = new Set();
    notifications.forEach((n) => n.notification_type && set.add(n.notification_type));
    return Array.from(set).sort();
  }, [notifications]);

  const filtered = useMemo(() => {
    if (filter === 'all') return notifications;
    if (filter === 'unread') return notifications.filter((n) => !n.read);
    return notifications.filter((n) => n.notification_type === filter);
  }, [filter, notifications]);

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  // Map a raw notification_type → human-friendly label + tone for the chip.
  const typeMeta = (t) => {
    const base = (t || 'system').replace(/_/g, ' ');
    const label = base
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    if (t?.startsWith('leave_')) return { label, cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
    if (t?.startsWith('claim_')) return { label, cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' };
    if (t?.startsWith('overtime_')) return { label, cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };
    if (t?.startsWith('payslip_') || t?.startsWith('payroll_')) return { label, cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300' };
    if (t?.startsWith('attendance_')) return { label, cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' };
    if (t?.startsWith('signature_') || t?.startsWith('document_')) return { label, cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' };
    return { label, cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' };
  };

  const formatWhen = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      const diffMs = Date.now() - d.getTime();
      const diffMin = Math.round(diffMs / 60000);
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.round(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      return d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch {
      return String(ts);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="notifications-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Bell className="h-7 w-7 text-emerald-500" />
              Notifications
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {unreadCount > 0
                ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
                : 'You are all caught up.'}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition"
              data-testid="notifications-mark-all-read-btn"
            >
              <CheckCheck className="h-4 w-4" />
              Mark all as read
            </button>
          )}
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="notifications-filter-chips-container">
          {[
            { id: 'all', label: 'All' },
            { id: 'unread', label: `Unread${unreadCount ? ` (${unreadCount})` : ''}` },
            ...availableTypes.map((t) => ({ id: t, label: typeMeta(t).label })),
          ].map((chip) => (
            <button
              type="button"
              key={chip.id}
              onClick={() => setFilter(chip.id)}
              data-testid={`notifications-filter-${chip.id}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filter === chip.id
                  ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:border-slate-100'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-700'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm divide-y divide-slate-100 dark:divide-slate-700">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500 dark:text-slate-400">
              Loading notifications…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center" data-testid="notifications-empty-state">
              <Inbox className="h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {filter === 'all'
                  ? 'No notifications yet.'
                  : 'No notifications match this filter.'}
              </p>
            </div>
          ) : (
            filtered.map((n) => {
              const meta = typeMeta(n.notification_type);
              return (
                <div
                  key={n.notification_id}
                  data-testid={`notifications-row-${n.notification_id}`}
                  onClick={() => handleRowClick(n)}
                  className={`group flex items-start gap-3 p-4 cursor-pointer transition hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                    !n.read ? 'bg-emerald-50/40 dark:bg-emerald-900/10' : ''
                  }`}
                >
                  <div className="mt-0.5">{getSeverityIcon(n.severity)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${meta.cls}`}>
                        {meta.label}
                      </span>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {n.title}
                      </p>
                      {!n.read && (
                        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" data-testid="notifications-unread-dot" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                      {n.message}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      {formatWhen(n.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
